import React, { useEffect, useMemo, useState } from "react";
import { useJsApiLoader } from "@react-google-maps/api";
import { useHistory } from "react-router-dom";
import { Box, Drawer, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";

import { PRIVATE_ROUTES, PUBLIC_ROUTES } from "../../../constants";
import { useDumontPanel } from "../../../context/DumontPanelContext";
import { useAppState } from "../../../context/StateContext";
import { useSecretManager } from "../../../hooks/useSecretManager";
import { Routes } from "../../../util/enums";
import env from "../../../util/env";
import DumontSidebar from "../../DumontSidebar";
import { DumontAuthGate } from "../../DumontSidebar/DumontAuthGate";
import { ElevationHeader } from "../ElevationHeader";
import { Footer as NewFooter } from "../NewFooter";
import Footer from "../Footer";
import useStyles from "./styles";
import { COLLAPSED_WIDTH, EXPANDED_WIDTH } from "Components/SideBar/constants";
import { SIDEBAR_TOTAL_WIDTH } from "Components/DumontSidebar/constants";
import Sidebar from "Components/SideBar";
import { isMoving, isProductionShipeeziTestCompany } from "Util/validation";
import { LogoContainer, Title } from "Components/DumontSidebar/components/FullscreenSidebar/styled";
import DumontLogo from "../Icons/IconDumont";
import FeatureLocked from "Components/FeatureLocked";

const DUMONT_PANEL_WIDTH = "40vw";

type Props = { children?: React.ReactNode };

interface PropsApiLoader {
  onChange?: (isLoaded: boolean) => void;
  googleMapsApiKey: string;
}

const libraries: ("drawing" | "geometry" | "places" | "visualization")[] = ["places"];

const JsApiLoader = ({ onChange, googleMapsApiKey }: PropsApiLoader) => {
  const { isLoaded } = useJsApiLoader({ libraries, googleMapsApiKey });
  useEffect(() => {
    onChange?.(isLoaded);
  }, [isLoaded, onChange]);
  return null;
};

const RctAppLayout = ({ children }: Props) => {
  const [sidebarPinned, setSidebarPinned] = useState(false);
  const contentOffset = sidebarPinned ? EXPANDED_WIDTH : COLLAPSED_WIDTH;

  const panelWidth = SIDEBAR_TOTAL_WIDTH;

  const { secret } = useSecretManager(env.SECRET_ID);
  const { setMapIsLoaded, currentUser } = useAppState();
  const { isPanelOpen, closePanel } = useDumontPanel();

  const history = useHistory();
  const classes = useStyles();

  const path = history.location.pathname;

  const renderApiLoader = useMemo(() => {
    if (secret?.GOOGLE_MAPS_API_KEY) {
      return (
        <JsApiLoader googleMapsApiKey={secret.GOOGLE_MAPS_API_KEY} onChange={setMapIsLoaded} />
      );
    }
    return null;
  }, [secret?.GOOGLE_MAPS_API_KEY, setMapIsLoaded]);

  const isSpecialFullPage =
    path === PRIVATE_ROUTES.CHAT ||
    path.startsWith(PRIVATE_ROUTES.MAIL) ||
    path === PRIVATE_ROUTES.TODO;

  const isPublicStandalone =
    path.includes("shipments-tracking-client") ||
    path.includes(PUBLIC_ROUTES.SHIPMENT_TRACKING_CLIENT) ||
    path.includes(Routes.ESTIMATE_QUOTE) ||
    path.includes(PUBLIC_ROUTES.SUPPLIER_ACCEPT_INVITE) ||
    path.includes(PUBLIC_ROUTES.ACCEPT_SHIPEEZI_NETWORK_INVITE) ||
    path.includes(PUBLIC_ROUTES.REJECT_SHIPEEZI_NETWORK_INVITE) ||
    path.includes(PUBLIC_ROUTES.ACCEPT_TASK_INVITE) ||
    path.includes(PUBLIC_ROUTES.REJECT_TASK_INVITE);

  const shouldHideMenuAndFooter =
    path.includes("videocall") ||
    path.includes(Routes.ACCEPT_INVITE) ||
    isSpecialFullPage ||
    isPublicStandalone;

  const renderPage = () => {
    if (isSpecialFullPage) {
      return <div className="rct-page-content p-0">{children}</div>;
    }

    if (isPublicStandalone) {
      return (
        <div
          className={classes.mainDiv}
          style={{
            marginRight: isPanelOpen ? panelWidth : 0,
            transition: "margin-right 0.2s ease",
          }}
        >
          {renderApiLoader}
          <div className={classes.childrenDiv}>{children}</div>
        </div>
      );
    }

    if (history.location.pathname.includes(PUBLIC_ROUTES.SHIPMENT_TRACKING_CLIENT)) return <></>;

    if (history.location.pathname.includes(Routes.ESTIMATE_QUOTE)) return <></>;

    if (history.location.pathname.includes(PUBLIC_ROUTES.SUPPLIER_ACCEPT_INVITE)) return <></>;

    if (history.location.pathname.includes(PUBLIC_ROUTES.ACCEPT_SHIPEEZI_NETWORK_INVITE))
      return <></>;

    if (history.location.pathname.includes(PUBLIC_ROUTES.REJECT_SHIPEEZI_NETWORK_INVITE))
      return <></>;

    if (history.location.pathname.includes(PUBLIC_ROUTES.ACCEPT_TASK_INVITE)) return <></>;

    if (history.location.pathname.includes(PUBLIC_ROUTES.REJECT_TASK_INVITE)) return <></>;

    return (
      <div className={classes.mainDiv}>
        {renderApiLoader}

        {isMoving(currentUser?.company) ? (
          <div className={classes.contentRow}>
            {!shouldHideMenuAndFooter && <Sidebar onPinnedChange={setSidebarPinned} />}
            <div
              className={classes.contentArea}
              style={{
                marginLeft: history.location.pathname.includes("videocall") ? 0 : contentOffset,
                marginRight: isPanelOpen ? panelWidth : 0,
                transition: "margin-right 0.3s ease-in-out",
              }}
            >
              <div className={classes.childrenDiv}>{children}</div>
            </div>
          </div>
        ) : (
          <div
            className={classes.childrenDiv}
            style={{
              marginRight: isPanelOpen ? panelWidth : 0,
              transition: "margin-right 0.3s ease-in-out",
            }}
          >
            {children}
          </div>
        )}

        {!shouldHideMenuAndFooter &&
          (isMoving(currentUser?.company) ? <NewFooter leftMargin={contentOffset} /> : <Footer />)}
      </div>
    );
  };

  const handleMaximize = () => {
    // Close the Ask Dumont panel and navigate to the full-page Dumont AI route
    closePanel();
    history.push(PRIVATE_ROUTES.DUMONT_AI);
  };

  return (
    <>
      <ElevationHeader sidebarPinned={sidebarPinned}>{renderPage()}</ElevationHeader>
      {isMoving(currentUser?.company) && (
        <Drawer
          anchor="right"
          open={isPanelOpen}
          onClose={closePanel}
          variant="persistent"
          hideBackdrop
          PaperProps={{
            sx: {
              width: panelWidth,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              minHeight: 0,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 1.5,
                py: 1,
                borderBottom: 1,
                borderColor: "divider",
                flexShrink: 0,
              }}
            >
              <Typography variant="subtitle1" fontWeight={600}>
                <LogoContainer sx={{ flex: 1, minWidth: 0 }}>
                  <DumontLogo size={20} />
                  <Title sx={{ fontSize: 20 }}>Dumont</Title>
                </LogoContainer>
              </Typography>
              <Box>
                <IconButton size="small" onClick={handleMaximize} aria-label="Maximize">
                  <OpenInFullIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={closePanel} aria-label="Close">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {env.NODE_ENV === "production" &&
              !isProductionShipeeziTestCompany(currentUser?.company?.id ?? 0) ? (
                <FeatureLocked featureLabel="Dumont AI" variant="sidebar" />
              ) : (
                <DumontAuthGate>
                  <DumontSidebar
                    isOpen={true}
                    onClose={closePanel}
                    embedded
                    onRequestFullPage={handleMaximize}
                  />
                </DumontAuthGate>
              )}
            </Box>
          </Box>
        </Drawer>
      )}
    </>
  );
};

export default RctAppLayout;
